import { create } from 'zustand';

/** Phase shown in the chat footer while a turn is live. `null` = idle. */
export type RunPhase = 'connecting' | 'thinking' | 'streaming' | { running: string };

interface Run {
  phase: RunPhase;
  controller: AbortController;
}

interface AgentRunStore {
  /** Active runs keyed by chat frame id. Ephemeral — never persisted/undone. */
  runs: Record<string, Run>;
  start: (frameId: string, controller: AbortController) => void;
  setPhase: (frameId: string, phase: RunPhase) => void;
  end: (frameId: string) => void;
}

export const useAgentRunStore = create<AgentRunStore>((set) => ({
  runs: {},
  start: (frameId, controller) =>
    set((s) => ({ runs: { ...s.runs, [frameId]: { phase: 'connecting', controller } } })),
  setPhase: (frameId, phase) =>
    set((s) => {
      const cur = s.runs[frameId];
      if (!cur) return s;
      return { runs: { ...s.runs, [frameId]: { ...cur, phase } } };
    }),
  end: (frameId) =>
    set((s) => {
      if (!s.runs[frameId]) return s;
      const next = { ...s.runs };
      delete next[frameId];
      return { runs: next };
    }),
}));

/**
 * Human label for the footer "thinking" indicator. No trailing ellipsis — the
 * animated .cf-dots beside it supplies that. Alive voice (styleguide §18):
 * the working state reads "Bringing it to life".
 */
export function phaseLabel(phase: RunPhase): string {
  if (typeof phase === 'object') return `Running ${phase.running}`;
  switch (phase) {
    case 'connecting':
      return 'Waking Viv';
    case 'thinking':
      return 'Thinking it through';
    case 'streaming':
      return 'Bringing it to life';
  }
}
